import type { BillingSummary } from '@/api/payments';

export interface ComputedBanner {
  tone: 'danger' | 'warning' | 'info' | 'success';
  text: string;
}

/**
 * Fuller than the sidebar's server-driven banner (see
 * components/layout/SubscriptionBanner.tsx), which stays silent once a
 * subscription is healthy — right for a persistent strip the pharmacist sees
 * on every page, wrong for a page whose entire purpose is showing billing
 * status. This adds the missing "active, N days left" state.
 */
export function computeBanner(pharmacy: BillingSummary): ComputedBanner {
  const now = Date.now();
  const endIso = pharmacy.plan === 'Trial' ? pharmacy.trialEndsAt : pharmacy.subscriptionEndsAt;
  const end = endIso ? new Date(endIso).getTime() : null;
  const msLeft = end !== null ? end - now : null;

  if (pharmacy.status === 'Suspended') {
    return { tone: 'danger', text: 'Your account is suspended. Please contact support to reactivate.' };
  }

  if (msLeft !== null && msLeft < 0) {
    return {
      tone: 'danger',
      text: `Your ${pharmacy.plan === 'Trial' ? 'trial' : 'subscription'} has expired. Renew now to restore full access.`,
    };
  }

  if (msLeft !== null && msLeft < 86_400_000 * 3) {
    const hoursLeft = Math.ceil(msLeft / 3_600_000);
    const daysLeft = Math.ceil(msLeft / 86_400_000);
    return {
      tone: 'warning',
      text:
        msLeft < 86_400_000
          ? `Only ${hoursLeft} hour(s) left in your ${pharmacy.plan.toLowerCase()}. Renew now to avoid interruption.`
          : `Only ${daysLeft} day(s) left in your ${pharmacy.plan.toLowerCase()}. Renew now.`,
    };
  }

  if (pharmacy.plan === 'Trial' && msLeft !== null) {
    const hoursLeft = Math.ceil(msLeft / 3_600_000);
    const daysLeft = Math.ceil(msLeft / 86_400_000);
    return {
      tone: 'info',
      text: msLeft < 86_400_000 ? `${hoursLeft} hour(s) left in your free trial.` : `${daysLeft} day(s) left in your free trial.`,
    };
  }

  const daysLeft = msLeft !== null ? Math.ceil(msLeft / 86_400_000) : null;
  return {
    tone: 'success',
    text: daysLeft !== null ? `Subscription active. ${daysLeft} day(s) remaining.` : 'Subscription active.',
  };
}
